<template>
  <div class="resetForm-wrapper">
    <!-- Token doğrulanıyor -->
    <v-form
      v-if="status === 'verifying'"
      class="resetForm pa-12 ma-auto text-center">
      <div class="resetForm-header mb-12">
        <div class="resetForm-header-logo">
          <img src="@/assets/app-logo.png" alt="Mank Logo">
        </div>
        <div class="resetForm-header-title">
          <h2 class="text-h5 text-bold">
            Verifying link
          </h2>
          <p class="text-h7 text--secondary mt-2">
            Please wait...
          </p>
        </div>
      </div>
      <v-progress-circular
        indeterminate
        color="primary"
        size="48"
        class="mb-6"/>
    </v-form>

    <!-- Token süresi dolmuş / geçersiz -->
    <v-form
      v-else-if="status === 'expired'"
      class="resetForm pa-12 ma-auto text-center">
      <div class="resetForm-header mb-12">
        <div class="resetForm-header-logo">
          <img src="@/assets/app-logo.png" alt="Mank Logo">
        </div>
        <div class="resetForm-header-title">
          <h2 class="text-h5 text-bold error--text">
            Link expired or invalid
          </h2>
          <p class="text-h7 text--secondary mt-2">
            This reset link has expired or is no longer valid. Request a new link to reset your password.
          </p>
        </div>
      </div>
      <v-btn
        color="primary"
        block
        elevation="0"
        class="mt-6"
        @click="$router.push('/forgot-password')">
        Request new link
      </v-btn>
      <div class="mt-6 text-element-2">
        <span
          class="text-primary text-semibold cursor-pointer"
          @click="$router.push('/login')">
          Back to login
        </span>
      </div>
    </v-form>

    <!-- Token geçerli: yeni şifre formu -->
    <v-form
      v-else-if="status === 'valid'"
      ref="form"
      @submit.prevent="handleReset"
      class="resetForm pa-12 ma-auto text-center">
      <div class="resetForm-header mb-12">
        <div class="resetForm-header-logo">
          <img src="@/assets/app-logo.png" alt="Mank Logo">
        </div>
        <div class="resetForm-header-title">
          <h2 class="text-h5 text-bold">
            Set new password
          </h2>
          <p class="text-h7 text--secondary mt-2">
            Enter your new password below.
          </p>
        </div>
      </div>
      <div class="resetForm-body mb-12">
        <div class="resetForm-body-input mb-6">
          <div class="text-element-2 text-semibold text-left mb-4">
            New password
          </div>
          <MankTextField
            variant="solo"
            v-model="newPassword"
            placeholder="Enter new password"
            :rules="passwordRules"
            :type="showPassword ? 'text' : 'password'"
            @click:append="showPassword = !showPassword"
            required/>
        </div>
        <div class="resetForm-body-input mb-4">
          <div class="text-element-2 text-semibold text-left mb-4">
            Confirm password
          </div>
          <MankTextField
            variant="solo"
            v-model="confirmPassword"
            placeholder="Confirm new password"
            :rules="confirmRules"
            :type="showConfirmPassword ? 'text' : 'password'"
            @click:append="showConfirmPassword = !showConfirmPassword"
            required/>
        </div>
      </div>
      <v-btn
        color="primary"
        block
        :loading="loading"
        :disabled="!newPassword || !confirmPassword"
        elevation="0"
        type="submit"
        class="mt-6">
        Reset password
      </v-btn>
      <div class="mt-6 text-element-2">
        <span
          class="text-primary text-semibold cursor-pointer"
          @click="$router.push('/login')">
          Back to login
        </span>
      </div>
    </v-form>
  </div>
</template>

<script>
import AuthService from '@/services/AuthService';
import AnalyticsService from '@/services/AnalyticsService';
import { EVENTS } from '@/constants/analyticsEvents';

export default {
  name: 'ResetPassword',
  data() {
    return {
      status: 'verifying', // 'verifying' | 'expired' | 'valid'
      token: '',
      newPassword: '',
      confirmPassword: '',
      showPassword: false,
      showConfirmPassword: false,
      loading: false,
      passwordRules: [
        (v) => !!v || 'Password is required',
        (v) => (v && v.length >= 8) || 'Password must be at least 8 characters',
      ],
      confirmRules: [
        (v) => !!v || 'Please confirm your password',
        (v) => v === this.newPassword || 'Passwords do not match',
      ],
    };
  },
  watch: {
    newPassword() {
      this.$nextTick(() => {
        if (this.$refs.form) this.$refs.form.validate();
      });
    },
    confirmPassword() {
      this.$nextTick(() => {
        if (this.$refs.form) this.$refs.form.validate();
      });
    },
  },
  mounted() {
    this.token = this.$route.query.token || '';
    if (!this.token) {
      this.status = 'expired';
      return;
    }
    this.verifyToken();
  },
  methods: {
    async verifyToken() {
      try {
        const result = await AuthService.verifyResetToken(this.token);
        this.status = (result && result.valid === true) ? 'valid' : 'expired';
      } catch {
        this.status = 'expired';
      }
    },
    async handleReset() {
      if (!this.$refs.form.validate()) return;
      this.loading = true;
      try {
        await AuthService.resetPassword(this.token, this.newPassword);
        AnalyticsService.track(EVENTS.PASSWORD_RESET_COMPLETED);
        this.$store.dispatch('ui/showNotification', {
          type: 'success',
          message: 'Your password has been reset. You can now log in.',
        }, { root: true });
        this.$router.push('/login');
      } catch {
        // Hata mesajı interceptor/store tarafından gösteriliyor
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.resetForm-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
}

.resetForm {
  border-radius: 16px;
  width: 360px;
  background: $light-surface-2;
}

.cursor-pointer {
  cursor: pointer;
}

.resetForm ::v-deep .mank-input .v-input__slot {
  border: 1px solid $light-surface-3;
  border-radius: 8px;
}

.resetForm ::v-deep .mank-input.v-input--is-focused .v-input__slot {
  border-color: $light-surface-4;
}
</style>
